import {
    Eye,
    ImagePlus,
    UploadCloud,
    X,
} from "lucide-react";
import {
    ChangeEvent,
    useRef,
    useState,
} from "react";

type KycImageUploadProps = {
    id: string;
    label: string;
    value: string;
    fileName: string;
    uploading?: boolean;
    onSelectFile?: (file: File) => void;
    onChange: (
        imageUrl: string,
        fileName: string,
        file?: File
    ) => void;
    onDelete: () => void;
};

const KycImageUpload = ({
    id,
    label,
    value,
    fileName,
    uploading = false,
    onSelectFile,
    onChange,
    onDelete,
}: KycImageUploadProps) => {
    const inputRef =
        useRef<HTMLInputElement>(
            null
        );

    const [
        previewOpen,
        setPreviewOpen,
    ] =
        useState(false);

    const handleFile =
        (
            event: ChangeEvent<HTMLInputElement>
        ) => {
            const file =
                event.target.files?.[
                0
                ];

            if (
                !file
            ) {
                return;
            }

            const allowedTypes = [
                "image/jpeg",
                "image/png",
                "image/webp",
            ];

            if (
                !allowedTypes.includes(
                    file.type
                )
            ) {
                window.alert(
                    "Please upload JPG, PNG or WEBP image."
                );

                event.target.value =
                    "";

                return;
            }

            if (
                file.size >
                5 *
                1024 *
                1024
            ) {
                window.alert(
                    "Image size must be 5 MB or smaller."
                );

                event.target.value =
                    "";

                return;
            }

            if (onSelectFile) {
                onSelectFile(file);
            }

            const reader = new FileReader();

            reader.onload = () => {
                if (typeof reader.result === "string") {
                    onChange(
                        reader.result,
                        file.name,
                        file
                    );
                }
            };

            reader.readAsDataURL(file);
            event.target.value = "";
        };

    const resolvedValue = value
        ? (value.startsWith("http") || value.startsWith("data:")
            ? value
            : `${process.env.REACT_APP_API_BASE_URL || ""}${value}`)
        : "";

    return (
        <>
            <div className="user-kyc-upload-field">
                <label>{label}</label>

                <input
                    ref={inputRef}
                    id={id}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleFile}
                    hidden
                />

                {!value ? (
                    <button
                        type="button"
                        className="user-kyc-drop-zone"
                        disabled={uploading}
                        onClick={() => inputRef.current?.click()}
                    >
                        <span className="user-kyc-drop-icon">
                            <UploadCloud size={25} strokeWidth={1.7} />
                        </span>

                        <strong>
                            {uploading ? "Uploading..." : `Upload ${label}`}
                        </strong>

                        <small>
                            JPG, PNG or WEBP • max 5 MB
                        </small>
                    </button>
                ) : (
                    <div className="user-kyc-uploaded-wrap">
                        <div className="user-kyc-uploaded-preview">
                            <button
                                type="button"
                                className="user-kyc-uploaded-image"
                                onClick={() => setPreviewOpen(true)}
                                aria-label={`Preview ${label}`}
                            >
                                <img
                                    src={resolvedValue}
                                    alt={label}
                                />

                                <span className="user-kyc-image-hover">
                                    <Eye
                                        size={
                                            17
                                        }
                                        strokeWidth={
                                            2
                                        }
                                    />

                                    Preview
                                </span>
                            </button>

                            <button
                                type="button"
                                className="user-kyc-image-delete"
                                onClick={
                                    onDelete
                                }
                                aria-label={`Delete ${label}`}
                            >
                                <X
                                    size={
                                        14
                                    }
                                    strokeWidth={
                                        2.4
                                    }
                                />
                            </button>
                        </div>

                        <div className="user-kyc-uploaded-copy">
                            <ImagePlus
                                size={
                                    15
                                }
                                strokeWidth={
                                    1.9
                                }
                            />

                            <span>
                                {
                                    fileName
                                }
                            </span>

                            <button
                                type="button"
                                onClick={() =>
                                    inputRef.current?.click()
                                }
                            >
                                Replace
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {previewOpen &&
                value && (
                    <div
                        className="user-kyc-image-modal-backdrop"
                        onClick={() =>
                            setPreviewOpen(
                                false
                            )
                        }
                    >
                        <div
                            className="user-kyc-image-modal"
                            onClick={(
                                event
                            ) =>
                                event.stopPropagation()
                            }
                        >
                            <button
                                type="button"
                                className="user-kyc-image-modal-close"
                                onClick={() =>
                                    setPreviewOpen(
                                        false
                                    )
                                }
                                aria-label="Close image preview"
                            >
                                <X
                                    size={
                                        18
                                    }
                                    strokeWidth={
                                        2
                                    }
                                />
                            </button>

                            <img
                                src={resolvedValue}
                                alt={`${label} full preview`}
                            />
                        </div>
                    </div>
                )}
        </>
    );
};

export default KycImageUpload;
